
import React, { useEffect, useState } from "react";
import { TableBody, TableRow, TableCell } from "@/components/ui/table";
import { useTranslation } from "react-i18next";

interface ScoresState {
  [key: string]: (number | null)[];
}

// Define the LevelInfo type to match what's passed from ResultsTable
interface LevelInfo {
  level: string;
  // Add any other properties that might be in the LevelInfo object
}

interface StandardTableBodyProps {
  testPoints: string[];
  scores: ScoresState;
  isSpecialPutts: boolean;
  getPointTotal: (point: string) => number;
  getGrandTotal: () => number;
  getTestDistances: (testId: string) => Record<string, string>;
  selectedTest: string | null;
  levels: Record<string, string | LevelInfo>;
  isWedgingTest?: boolean;
  isBunkerTest?: boolean;
}

const StandardTableBody: React.FC<StandardTableBodyProps> = ({
  testPoints,
  scores,
  isSpecialPutts,
  getPointTotal,
  getGrandTotal,
  getTestDistances,
  selectedTest,
  levels,
  isWedgingTest = false,
  isBunkerTest = false
}) => {
  const { t } = useTranslation('training');
  const [displayPoints, setDisplayPoints] = useState<string[]>([]);

  // Current path to determine level
  const currentPath = window.location.pathname;
  const isLevel1 = currentPath.includes('level1');
  const isLevel2 = currentPath.includes('level2');
  const isLevel3 = !isLevel1 && !isLevel2;
  
  const isPuttPlat = selectedTest === "putts-plat";
  const isChippingPlat = selectedTest === "chipping-plat";
  
  // Get distances from the getTestDistances function
  const distances = selectedTest ? getTestDistances(selectedTest) : {};
  
  useEffect(() => {
    // Use the testPoints provided by the parent component
    setDisplayPoints([...testPoints]);
  }, [testPoints]);
  
  // Format the distance for display
  const formatDistance = (point: string) => {
    // Use distances from the getTestDistances function if available
    if (distances[point]) {
      return distances[point];
    }
    
    // Level specific formatting
    if (isLevel1) {
      if (point === 'G') return '6m';
      if (point === 'H') return '10m';
      if (point === 'I') return '12m';
    } else if (isLevel2) {
      if (point === 'D') return '1,2m';
      if (point === 'E') return '1,5m';
      if (point === 'F') return '3m';
    } else if (isLevel3) {
      // Level 3 putting
      if (point === 'A') return '0,5m';
      if (point === 'B') return '0,8m';
      if (point === 'C') return '1m';
      
      // Level 3 chipping
      if (isChippingPlat) {
        if (point.includes('A')) return '7m';
        if (point.includes('B')) return '10m';
        if (point.includes('C')) return '20m';
      }
    }
    
    return point;
  };

  return (
    <TableBody>
      {displayPoints.map((point) => {
        // Ensure we have a valid array for this point's scores
        const pointScores = scores[point] || Array(10).fill(null);
        
        return (
          <TableRow key={point}>
            <TableCell className="font-medium">
              {point}
            </TableCell>
            <TableCell className="font-medium text-green-600">
              {formatDistance(point)}
            </TableCell>
            {Array.from({ length: 10 }, (_, i) => (
              <TableCell key={i} className="text-center">
                {pointScores[i] !== null && pointScores[i] !== undefined
                  ? pointScores[i] 
                  : '-'}
              </TableCell>
            ))}
            <TableCell className="text-center font-bold">
              {getPointTotal(point)}
            </TableCell>
          </TableRow>
        );
      })}
      <TableRow>
        <TableCell colSpan={2} className="text-right font-bold">
          {t('interface.tableHeaders.total')} {t('interface.scoring.points')}
        </TableCell>
        <TableCell colSpan={10} />
        <TableCell className="text-center font-bold">
          {getGrandTotal()}
        </TableCell>
      </TableRow>
    </TableBody>
  );
};

export default StandardTableBody;
